import { Component } from "react";
import type { ErrorInfo, ReactNode } from "react";
import {
  reportErrorToBackend,
  createReactErrorReport,
} from "../lib/errorReporting";
import { logger } from "../lib/logger";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log to console in development
    console.error("ErrorBoundary caught an error:", error, errorInfo);

    // Log to structured logger
    logger.error("react_error_boundary", {
      error: {
        message: error.message,
        name: error.name,
        stack: error.stack,
      },
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
    });

    // Update state with error info
    this.setState({
      error,
      errorInfo,
    });

    // Report error to backend telemetry endpoint
    const report = createReactErrorReport(
      error,
      errorInfo.componentStack || undefined,
    );
    reportErrorToBackend(report);
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-900 p-4">
          <div className="max-w-2xl w-full bg-gray-800 rounded-lg p-8 border-2 border-red-600">
            <div className="flex items-start gap-4 mb-6">
              <div className="text-red-500 text-4xl">⚠️</div>
              <div>
                <h1 className="text-2xl font-bold text-white mb-2">
                  Oops! Something went wrong
                </h1>
                <p className="text-gray-300">
                  An unexpected error occurred. Don't worry, your data is safe.
                </p>
              </div>
            </div>

            {/* Error Details (collapsed by default) */}
            {this.state.error && (
              <details className="bg-gray-900 p-4 rounded mb-6">
                <summary className="text-gray-300 cursor-pointer hover:text-white font-medium">
                  Technical Details (for developers)
                </summary>
                <div className="mt-4 space-y-2">
                  <div>
                    <span className="text-red-400 font-semibold">Error:</span>
                    <pre className="text-gray-300 text-sm mt-1 overflow-x-auto">
                      {this.state.error.toString()}
                    </pre>
                  </div>
                  {this.state.error.stack && (
                    <div>
                      <span className="text-red-400 font-semibold">
                        Stack Trace:
                      </span>
                      <pre className="text-gray-400 text-xs mt-1 overflow-x-auto max-h-64">
                        {this.state.error.stack}
                      </pre>
                    </div>
                  )}
                  {this.state.errorInfo?.componentStack && (
                    <div>
                      <span className="text-red-400 font-semibold">
                        Component Stack:
                      </span>
                      <pre className="text-gray-400 text-xs mt-1 overflow-x-auto max-h-64">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </div>
                  )}
                </div>
              </details>
            )}

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={this.handleReset}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
              >
                Try Again
              </button>
              <button
                onClick={() => (window.location.href = "/")}
                className="flex-1 bg-gray-700 hover:bg-gray-600 text-white px-6 py-3 rounded-lg font-medium transition-colors"
              >
                Return Home
              </button>
              <button
                onClick={() => window.location.reload()}
                className="bg-gray-700 hover:bg-gray-600 text-white px-6 py-3 rounded-lg font-medium transition-colors"
              >
                Reload Page
              </button>
            </div>

            {/* Help Text */}
            <div className="mt-6 text-sm text-gray-400">
              <p>If this problem persists:</p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Try clearing your browser cache and cookies</li>
                <li>
                  Make sure you're using the latest version of your browser
                </li>
                <li>Check the browser console for more details (F12)</li>
                <li>Contact support if the issue continues</li>
              </ul>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
